import { useState, useEffect, useCallback } from "react"
import { object, func } from "prop-types"
import {
  usePaymentIntent,
  usePaymentIntentMutation,
  useInvalidatePaymentIntent,
} from "source/registrations/hooks/usePaymentIntent"
import { loadStripe } from "@stripe/stripe-js/pure"
import { useOrganization } from "source/registrations/hooks/useOrganization"
import { Button } from "@planningcenter/doxy-web"
import StripeWrapper from "source/registrations/StripeWrapper"
import StripeForm from "source/registrations/StripeForm"
import { AlertMessage, Loading } from "source/shared/components"
import { api } from "source/registrations/api/client"
import ReviewPaymentType from "source/registrations/reservations/ReviewPaymentType"

const PaymentForm = ({ registration, registrationInvoice, onPaymentSave }) => {
  const [paymentIntentError, setPaymentIntentError] = useState(null)
  const [paymentIntent, setPaymentIntent] = useState(null)
  const [paymentType, setPaymentType] = useState("full")
  const [canMakePayment, setCanMakePayment] = useState(true)
  const [makePayment, setMakePayment] = useState(false)
  const [error, setError] = useState(null)
  const [isProcessing, setIsProcessing] = useState(null)
  const [stripe, setStripe] = useState(null)

  const {
    allowCreditCardPayments,
    hasPaymentsModule,
    allowMinimumDeposits,
    currency,
  } = registration.event

  const { data: organization } = useOrganization()
  const {
    paymentIntegration: {
      isConnected,
      isTestMode,
      publishableKey,
      accountIdentifier,
    } = {},
  } = organization

  const paymentIntentQuery = usePaymentIntent(registration.id, {
    initialData: registration.paymentIntent,
    retry: false,
  })

  const invalidatePaymentIntent = useInvalidatePaymentIntent(registration.id, {
    initialData: registration.paymentIntent,
    retry: false,
  })

  const mutation = usePaymentIntentMutation(registration.id, paymentIntent)

  const { balanceDue, balanceDueCents, minimumDueCents } = registrationInvoice

  const mutatePaymentIntent = useCallback(
    (amount) => {
      mutation.mutate(
        { amount: amount },
        {
          onSuccess: (data, _variables, _context) => {
            setCanMakePayment(true)
            setPaymentIntent(data)
            setPaymentIntentError(null)
          },
          onError: (error, _params, _context) => {
            setPaymentIntentError(error.detail)
            setCanMakePayment(false)
          },
        },
      )
    },
    [mutation],
  )

  useEffect(() => {
    setPaymentIntent(paymentIntentQuery.data)
  }, [paymentIntentQuery.data])

  useEffect(() => {
    if (paymentIntentQuery.isError) {
      mutatePaymentIntent(balanceDueCents)
    }
  }, [
    paymentIntentQuery.error,
    balanceDueCents,
    mutatePaymentIntent,
    paymentIntentQuery.isError,
  ])

  useEffect(() => {
    if (paymentIntentError) {
      setPaymentIntent(null)
      mutatePaymentIntent(balanceDueCents)
      setMakePayment(true)
    }
  }, [paymentIntentError, balanceDueCents, mutatePaymentIntent])

  useEffect(() => {
    const fetchStripe = async () => {
      const response = await loadStripe(publishableKey, {
        stripeAccount: accountIdentifier,
      })
      setStripe(response)
    }

    if (
      hasPaymentsModule &&
      allowCreditCardPayments &&
      publishableKey &&
      !stripe
    ) {
      fetchStripe()
    }
  }, [
    publishableKey,
    accountIdentifier,
    allowCreditCardPayments,
    hasPaymentsModule,
    stripe,
  ])

  const stillNeedMinimum = allowMinimumDeposits ? minimumDueCents > 0 : false

  const handleMakePayment = () => {
    if (balanceDueCents <= 0) return

    if (!paymentIntent || paymentIntent.amount !== balanceDueCents) {
      mutatePaymentIntent(balanceDueCents)
      setMakePayment(true)
    } else {
      setMakePayment(true)
    }
  }

  const handlePaymentTypeChange = async (value) => {
    if (value === "other") setCanMakePayment(false)
    let newAmount = null

    if (value === "full") {
      newAmount = balanceDueCents
    } else if (value === "minimum") {
      newAmount = minimumDueCents
    }

    setPaymentType(value)

    if (newAmount) {
      mutatePaymentIntent(newAmount)
    }
  }

  const handleOtherAmountChange = async (value) => {
    let error = null
    const otherAmount = Number(value) * 100

    if (!value) {
      error = "Other amount can not be blank"
    } else if (otherAmount < 50) {
      error = `Other amount must be at least ${currency}0.50`
    } else if (otherAmount < minimumDueCents || otherAmount > balanceDueCents) {
      error = "Other amount must be between minimum and full amount"
    }

    if (error) {
      setError(error)
      setCanMakePayment(false)
    } else {
      setError(null)
      mutatePaymentIntent(otherAmount)
    }
  }

  const handlePaymentSave = (stripeResponse) => {
    const path = `/registrations/v2/registrations/${registration.id}/capture_payment`
    return api
      .post(path, {
        data: {
          attributes: {
            status: stripeResponse?.status,
            amount: stripeResponse?.amount,
            paymentIntentId: stripeResponse?.id,
            method: "creditCard",
          },
        },
      })
      .then(() => {
        setPaymentType("full")
        setMakePayment(false)
        setIsProcessing(false)
        setPaymentIntent(null)
        invalidatePaymentIntent()
        registration.invalidate()
        onPaymentSave()
      })
      .catch((error) => {
        setIsProcessing(false)
        invalidatePaymentIntent()
        setError(error?.detail)
      })
  }

  const handleCancel = () => {
    setPaymentType("full")
    setMakePayment(false)
  }

  const handleIsProcessing = (is) => {
    setIsProcessing(is)
  }

  // TODO:
  //   * Since we store the paymentIntents for a registerable, we need to confirm how long they are "good" for in stripe.
  //       - If they have a "lifespan", we'll need a way to cancel existing ones and create new (maybe rake task to clean up intents (x) old).
  //       - They can also be used ONLY once, so once it's been charged a new intent will need to be created.

  return (
    <div>
      {!makePayment && (
        <div className={"ta-c my-3"}>
          <Button onClick={handleMakePayment} text={"Make payment"} />
        </div>
      )}

      {makePayment && paymentIntent?.clientSecret && (
        <div>
          {allowMinimumDeposits && (
            <ReviewPaymentType
              onChange={handlePaymentTypeChange}
              onOtherAmountChange={handleOtherAmountChange}
              paymentType={paymentType}
              totalDueString={balanceDue}
              minimumDue={minimumDueCents}
              minimumDueString={registration.minimumDueString}
              isPaymentUpdating={mutation.isLoading}
              onCanMakePayment={setCanMakePayment}
              currencySymbol={currency}
              showMinimumOption={stillNeedMinimum}
              showLaterOption={false}
            />
          )}
          <StripeWrapper
            stripe={stripe}
            paymentIntent={paymentIntent}
            error={paymentIntentError}
          >
            {isConnected && allowCreditCardPayments && (
              <>
                {!stripe ? (
                  <Loading />
                ) : (
                  <div className="mt-3">
                    <StripeForm
                      responsiblePerson={registration.responsiblePerson}
                      paymentIntent={paymentIntent}
                      onPaymentSave={handlePaymentSave}
                      onIsProcessing={handleIsProcessing}
                      amount={paymentIntent?.amount}
                      onError={setError}
                      isTestMode={isTestMode}
                      isLoading={mutation.isLoading || isProcessing}
                    />
                  </div>
                )}
              </>
            )}
          </StripeWrapper>
          {error && (
            <AlertMessage className="mt-2">
              <div style={{ color: "var(--color-ruby)" }}>{error}</div>
            </AlertMessage>
          )}
          {stripe && paymentIntent?.clientSecret && (
            <div className={"mt-2 d-f ai-c jc-sb fw-500"}>
              <Button
                onClick={handleCancel}
                text={"Cancel"}
                variant={"outline"}
              />
              <button
                className="btn"
                form="paymentForm"
                disabled={!canMakePayment || mutation.isLoading || isProcessing}
              >
                {isProcessing
                  ? "Processing payment..."
                  : `Pay ${
                      paymentIntent?.amount
                        ? `${paymentIntent.amountFormatted}`
                        : ""
                    }`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

PaymentForm.propTypes = {
  registration: object.isRequired,
  registrationInvoice: object.isRequired,
  onPaymentSave: func.isRequired,
}

export default PaymentForm
